import React from 'react';
import { SiWhatsapp } from 'react-icons/si';
import { CircleDisplay } from 'src/modules/shared/components/atoms/circle-display';
import type { ComponentSize } from 'src/modules/shared/types/component.type';

export type SupportedProviderBrandId =
  | 'whatsapp'
  | 'meli'
  | '2clics'
  | 'wasi'
  | 'toppropiedades'
  | 'kiteprop';

interface ProviderBrandAvatarProps {
  providerId: SupportedProviderBrandId;
  size?: ComponentSize;
}

const BRAND_ASSET_BY_PROVIDER: Partial<
  Record<SupportedProviderBrandId, string>
> = {
  meli: '/integrations/meli-logo.png',
  '2clics': '/integrations/2clics-logo.svg',
  wasi: '/integrations/wasi-isotype.svg',
  toppropiedades: '/integrations/toppropiedades-logo.png',
  kiteprop: '/integrations/kiteprop-logo.png',
};

const BRAND_VARIANT_BY_PROVIDER: Record<
  SupportedProviderBrandId,
  | 'whatsapp-subtle'
  | 'meli-subtle'
  | '2clics-subtle'
  | 'wasi-subtle'
  | 'toppropiedades-subtle'
  | 'kiteprop-subtle'
> = {
  whatsapp: 'whatsapp-subtle',
  meli: 'meli-subtle',
  '2clics': '2clics-subtle',
  wasi: 'wasi-subtle',
  toppropiedades: 'toppropiedades-subtle',
  kiteprop: 'kiteprop-subtle',
};

/**
 * Reusable branded provider avatar for integrations and publication surfaces.
 */
export const ProviderBrandAvatar: React.FC<ProviderBrandAvatarProps> = ({
  providerId,
  size = 'md',
}) => {
  if (providerId === 'whatsapp') {
    return (
      <CircleDisplay
        icon={<SiWhatsapp />}
        variant={BRAND_VARIANT_BY_PROVIDER[providerId]}
        size={size}
      />
    );
  }

  const brandAsset = BRAND_ASSET_BY_PROVIDER[providerId];

  if (!brandAsset) {
    return (
      <CircleDisplay
        text={providerId === '2clics' ? '2 C' : providerId}
        variant={BRAND_VARIANT_BY_PROVIDER[providerId]}
        size={size}
      />
    );
  }

  return (
    <CircleDisplay
      variant={BRAND_VARIANT_BY_PROVIDER[providerId]}
      size={size}
      icon={
        <img
          src={brandAsset}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-contain"
        />
      }
    />
  );
};
